import { Link, useLocation } from '@remix-run/react'
import { useAtomValue } from 'jotai'
import { aOrganization } from '~/atoms'
import { Badge, Button } from '~/components/ui'
import { getListingTypeLabel, type Listing } from '~/models'
import { cn, fmtAddress, getPropertyTypeClassName, STATUSES } from '~/utils'

export default function PrivateListing({ listing }: { listing: Listing }) {
  const organization = useAtomValue(aOrganization)
  const location = useLocation()

  return (
    <main className="px-4 py-8">
      <div className="mx-auto min-h-[calc(100vh-20rem)] max-w-6xl">
        <div className="flex flex-col items-start justify-between gap-x-8 gap-y-2 sm:flex-row">
          <div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex items-center justify-between gap-8 sm:items-start">
                <Badge className="text-base hover:bg-primary sm:mt-1">
                  {STATUSES[listing.status]}
                </Badge>
              </div>
              <h1 className="text-3xl font-medium text-foreground">
                {listing.name || listing.property.address}
              </h1>
            </div>
            <div className="flex items-center justify-between">
              <p className="mt-1 text-xl font-light text-muted-foreground">
                {fmtAddress(listing.property, {
                  locationVisibility: listing.locationVisibility,
                })}
              </p>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <h2 className="mr-2 text-2xl font-medium">
              {getListingTypeLabel(listing)}
            </h2>
            {listing.isBusinessListing && (
              <Badge className="bg-[#a21caf] text-base hover:bg-[#a21caf]">
                Business
              </Badge>
            )}
            {listing.propertyTypes.map((value, index) => {
              return (
                <Badge
                  key={index}
                  className={cn('text-base', getPropertyTypeClassName(value))}>
                  <span>{value}</span>
                </Badge>
              )
            })}
          </div>
        </div>
        <div className="mt-8 rounded-md border border-border/50 bg-muted/50 p-4 text-center sm:mt-16 sm:p-6">
          {organization!.meta.logo && (
            <img
              src={organization!.meta.logo.url}
              className="mx-auto mb-6 max-w-56"
            />
          )}
          <p className="text-xl">
            This listing is only available to members of{' '}
            <b>{organization!.name}</b>. If you're a member, log in using the
            button below.
          </p>
          <Button asChild size="lg" className="mt-6">
            <Link to={`/login?redirect=${location.pathname}`}>Log in</Link>
          </Button>
        </div>
      </div>
    </main>
  )
}
